/**
 * Filter lands based on various criteria
 * @param {Array} lands - All lands to filter
 * @param {Object} filters - Filter criteria
 * @param {Object} filters.selectedTag - Single selected tag
 * @param {Array} filters.selectedTags - Multiple selected tags (AND logic)
 * @param {Object} filters.selectedLandType - Selected land type filter
 * @param {String} filters.searchQuery - Search query string
 * @param {Number} [filters.priceMin] - Minimum price (KWD)
 * @param {Number} [filters.priceMax] - Maximum price (KWD)
 * @returns {Array} Filtered lands
 */
export const filterLands = (lands, filters) => {
  const { selectedTag, selectedTags = [], selectedLandType, searchQuery, priceMin, priceMax } = filters;

  let result = [...lands];

  if (priceMin != null) {
    result = result.filter(land => land.price != null && Number(land.price) >= priceMin);
  }
  if (priceMax != null) {
    result = result.filter(land => land.price != null && Number(land.price) <= priceMax);
  }

  // Filter by single tag — if selectedTag is a parent tag (has childTagIds),
  // match lands tagged with the parent itself OR any of its children.
  if (selectedTag) {
    const matchIds = selectedTag.childTagIds && selectedTag.childTagIds.length > 0
      ? [selectedTag.id, ...selectedTag.childTagIds]
      : [selectedTag.id];
    result = result.filter(land =>
      land.tags && land.tags.some(tag => matchIds.includes(tag.id))
    );
  }

  // Filter by multiple tags (AND logic - land must have all selected tags)
  if (selectedTags.length > 0) {
    result = result.filter(land => {
      if (!land.tags) return false;
      const landTagTitles = land.tags.map(tag => tag.title);
      return selectedTags.every(selectedTagTitle => 
        landTagTitles.includes(selectedTagTitle)
      );
    });
  }

  // Filter by land type
  if (selectedLandType) {
    result = result.filter(land => 
      land.land_type_info && land.land_type_info.name === selectedLandType.name
    );
  }

  // Filter by search query
  if (searchQuery) {
    const query = searchQuery.toLowerCase();
    result = result.filter(land =>
      (land.code_number && land.code_number.toLowerCase().includes(query)) ||
      (land.land_number && land.land_number.toLowerCase().includes(query)) ||
      (land.notes && land.notes.toLowerCase().includes(query)) ||
      (land.tags && land.tags.some(tag => tag.title.toLowerCase().includes(query)))
    );
  }

  return result;
};

/**
 * Count lands by tag
 * @param {Array} lands - All lands
 * @param {Number} tagId - Tag ID to count
 * @returns {Number} Count of lands with the tag
 */
export const getLandsByTag = (lands, tagId) => {
  return lands.filter(land => land.tags && land.tags.some(tag => tag.id === tagId)).length;
};
